import type {
  ColoredMaterial,
  ColoredMaterialResult,
} from "@color-finder/color-finder-api/models";
import type { Dataset } from "models/colors";
import { useDataset } from "providers/DatasetProvider";
import { useEffect, useRef, useState } from "react";
import { appendDatasetParam } from "service/utils/dataset";
import { getAuthenticatedAxios } from "./authentication";

const buildTextSearchParams = (query: string, dataset: Dataset) => {
  const params = new URLSearchParams();
  params.set("textSearch", query.trim());
  params.set("limit", "50");
  params.set("page", "0");
  appendDatasetParam(params, dataset);
  return params;
};

/**
 * Custom hook for product search with server-side text search
 * @param searchQuery - The search query string
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 * @param enabled - Whether search is enabled (default: true)
 */
export function useProductSearch(
  searchQuery: string,
  debounceMs: number = 300,
  enabled: boolean = true,
): {
  results: ColoredMaterial[];
  isLoading: boolean;
  error: Error | undefined;
  totalCount: number;
} {
  const { axios } = getAuthenticatedAxios();
  const { dataset } = useDataset();
  const [results, setResults] = useState<ColoredMaterial[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const abortControllerRef = useRef<AbortController>();

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, debounceMs);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery, debounceMs]);

  // Perform server-side search when debounced query changes
  useEffect(() => {
    if (!enabled || !debouncedQuery || debouncedQuery.trim().length < 2) {
      setResults([]);
      setTotalCount(0);
      return;
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsLoading(true);
    setError(undefined);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    (async () => {
      try {
        // Use server-side text search with textSearch parameter
        const params = buildTextSearchParams(debouncedQuery, dataset);

        const response = await axios.get<ColoredMaterialResult>(
          `/v0/color-search/?${params.toString()}`,
          { signal: controller.signal },
        );

        if (!controller.signal.aborted) {
          setResults(response.data.results || []);
          setTotalCount(response.data.totalCount || 0);
          setIsLoading(false);
        }
      } catch (e: any) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          setError(e);
          setResults([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [axios, debouncedQuery, enabled, dataset]);

  return {
    results,
    isLoading,
    error,
    totalCount,
  };
}
